import { API_BASE_URL } from './apiUtils';
import { ApiResource } from './ApiResource';

export interface ReceiptTexts {
    title: string;
    description1: string;
    description2: string;
    description1Family: string;
    description2Family: string;
    warningDuplicatesHeader: string;
    warningDuplicates: string;
    warningFailedHeader: string;
    warningFailed: string;
}

export async function fetchReceiptTexts(): Promise<ReceiptTexts> {
    return {
        title: 'Velkommen',
        description1: 'Vi sender deg medlemskort på e-post.',
        description2: 'Tusen takk for at du meldte deg inn i Skiforeningen. Sammen setter vi spor.',
        description1Family: 'Vi sender deg medlemskortene til alle i familien på e-post.',
        description2Family: 'Tusen takk for at dere meldte dere inn i Skiforeningen. Sammen setter vi spor.',
        warningDuplicatesHeader: 'Eksisterende medlemmer:',
        warningDuplicates:
            'Det kan se ut som at det allerede finnes et medlemskap på ett eller flere av familiemedlemmene. Ta kontakt på post@skiforeningen.no for å få knyttet dem til familiemedlemskapet.',
        warningFailedHeader: 'Medlemmer som ikke kunne registreres:',
        warningFailed:
            'Noe gikk galt ved registrering av ett eller flere av familiemedlemmene. Ta kontakt på post@skiforeningen.no for å få knyttet dem til familiemedlemskapet.',
    };
}

interface FamilyMember {
    fullName: string;
}

export interface ReceiptInfo {
    firstName: string;
    registeredFamilyMembers?: FamilyMember[];
    duplicateFamilyMembers?: FamilyMember[];
    failedFamilyMembers?: FamilyMember[];
}

export type ReceiptInfoResource = ApiResource<ReceiptInfo> | { status: 'no-content' };

export async function getReceiptInfo(reference: string): Promise<ReceiptInfoResource> {
    try {
        const response = await fetch(`${API_BASE_URL}/api/Receipt/${reference}`);

        if (response.status === 200) {
            return {
                status: 'ok',
                data: (await response.json()) as ReceiptInfo,
            };
        }

        if (response.status === 204) {
            return { status: 'no-content' };
        }

        return { status: 'error' };
    } catch (error) {
        return { status: 'error' };
    }
}

function delay(milliseconds: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, milliseconds));
}

export async function performActionWithAdjustedFibonacciBackoff<T>(
    action: () => Promise<T>,
    breakCriterion: (input: T) => boolean,
    initialDelayInMilliseconds: number,
    maxDelayInMilliseconds: number,
    timeoutInMinutes: number
): Promise<T> {
    const startTime = new Date().getTime();
    const ONE_MINUTE_MILLISECONDS = 60 * 1000;

    let timeElapsedInMillis = 0;
    let previousWaitTime = 0;
    let waitTime = initialDelayInMilliseconds;

    while (timeElapsedInMillis <= timeoutInMinutes * ONE_MINUTE_MILLISECONDS) {
        const result = await action();
        if (breakCriterion(result)) {
            return result;
        }
        const newWaitTime = waitTime + previousWaitTime / 2;
        previousWaitTime = waitTime;
        waitTime = Math.min(newWaitTime, maxDelayInMilliseconds);

        await delay(waitTime);
        timeElapsedInMillis = new Date().getTime() - startTime;
    }
    throw new Error(`Action timed out. Milliseconds elapsed: ${new Date().getTime() - startTime}`);
}
